/**
 * The standard fonts the addendum is drawn with (Helvetica, Times) are
 * WinAnsi-encoded, and pdf-lib throws on any glyph outside that encoding. The
 * forms are filled in English and Spanish, which WinAnsi covers entirely
 * (á é í ó ú ñ ü ¿ ¡ ...), so instead of embedding a Unicode font we bring
 * whatever else gets pasted in (Word, e-mails) down to that set.
 */

/** WinAnsi characters in 0x80-0x9F: the ones Latin-1 does not have. */
const WIN_ANSI_EXTRAS = "€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ";

const isWinAnsi = (char) => {
    const code = char.codePointAt(0);
    return (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff) || WIN_ANSI_EXTRAS.includes(char);
};

/**
 * Typographic variants rewritten silently. Most ARE WinAnsi, but the addendum
 * has always printed their plain form, so it keeps doing so.
 */
const TYPOGRAPHIC = [
    [/[‘’‚′‛]/g, "'"],
    [/[“”„″‟]/g, '"'],
    [/[‐‑‒–—―−]/g, "-"],
    [/…/g, "..."],
    [/[  -   　]/g, " "],
    [/[​-‍⁠﻿­]/g, ""],
    [/\t/g, "    "],
    [/\r\n?/g, "\n"],
];

/** Non-WinAnsi characters with an obvious WinAnsi stand-in. */
const SUBSTITUTES = {
    "●": "•",
    "◦": "•",
    "▪": "•",
    "■": "•",
    "‣": "•",
    "⁃": "•",
    "∙": "•",
    "→": "->",
    "←": "<-",
    "≤": "<=",
    "≥": ">=",
    "≠": "!=",
};

const applyTypographic = (text) => TYPOGRAPHIC.reduce((acc, [regex, replacement]) => acc.replace(regex, replacement), text);

/**
 * WinAnsi form of a single character: itself, a substitute, its decomposition
 * without diacritics ("ă" -> "a", "ﬁ" -> "fi"), or "" when nothing fits.
 */
const toWinAnsi = (char) => {
    if (char === "\n" || isWinAnsi(char)) return char;
    if (char in SUBSTITUTES) return SUBSTITUTES[char];

    const decomposed = char.normalize("NFKD").replace(/[̀-ͯ]/g, "");
    return decomposed && Array.from(decomposed).every(isWinAnsi) ? decomposed : "";
};

/** Text safe to draw with the standard fonts. */
export const toWinAnsiText = (value) =>
    Array.from(applyTypographic(String(value ?? "")))
        .map(toWinAnsi)
        .join("");

/**
 * What toWinAnsiText would change, beyond the silent typographic rewrites, so
 * the UI can tell the user before the PDF does.
 *
 * @returns {{ replaced: Array<{ char: string, with: string }>, removed: string[] }}
 */
export const inspectWinAnsiText = (value) => {
    const replaced = new Map();
    const removed = new Set();

    Array.from(applyTypographic(String(value ?? ""))).forEach((char) => {
        if (char === "\n" || isWinAnsi(char)) return;
        const result = toWinAnsi(char);
        if (result) replaced.set(char, result);
        else removed.add(char);
    });

    return {
        replaced: [...replaced].map(([char, replacement]) => ({ char, with: replacement })),
        removed: [...removed],
    };
};
